import { useFilters } from "@fluidattacks/design";
import type {
  IOptions,
  IOptionsProps,
  IUseFilterProps,
} from "@fluidattacks/design";
import capitalize from "lodash/capitalize";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { IFindingAttr } from "./types";

import { useFiltersStorage } from "storage/filters";
import { translate } from "utils/translations/translate";

const useEvidenceDraftsFilters = (
  findings: IFindingAttr[],
  setFilteredDataset: React.Dispatch<React.SetStateAction<IFindingAttr[]>>,
): IUseFilterProps<IFindingAttr> => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();

  const uniqueGroups = useMemo(
    (): string[] =>
      Array.from(
        new Set(
          findings.flatMap((finding): string => capitalize(finding.groupName)),
        ),
      ),
    [findings],
  );
  const uniqueTitles = useMemo(
    (): string[] =>
      Array.from(
        new Set(findings.flatMap((finding): string => finding.title ?? "")),
      ),
    [findings],
  );

  const options: IOptionsProps<IFindingAttr>[] = useMemo(
    (): IOptionsProps<IFindingAttr>[] => [
      {
        filterOptions: [
          {
            key: "groupName",
            label: t("organization.tabs.groups.newGroup.name.text"),
            options: uniqueGroups.map(
              (group): IOptions => ({ label: group, value: group }),
            ),
            type: "select",
          },
        ],
        label: t("organization.tabs.groups.newGroup.name.text"),
      },
      {
        filterOptions: [
          {
            key: "title",
            label: t("group.findings.type"),
            options: uniqueTitles.map(
              (title): IOptions => ({ label: title, value: title }),
            ),
            type: "checkboxes",
          },
        ],
        label: t("group.findings.type"),
      },
      {
        filterOptions: [
          {
            key: "hasEvidenceDraft",
            label: t(
              "todoList.tabs.evidenceDrafts.table.headers.hasEvidenceDraft",
            ),
            options: [
              {
                label: t(
                  "todoList.tabs.evidenceDrafts.table.hasLocationDrafts.yes",
                ),
                value: "true",
              },
              {
                label: t(
                  "todoList.tabs.evidenceDrafts.table.hasLocationDrafts.no",
                ),
                value: "false",
              },
            ],
            type: "radioButton",
          },
        ],
        label: t("todoList.tabs.evidenceDrafts.table.headers.hasEvidenceDraft"),
      },
      {
        filterOptions: [
          {
            key: "hasLocationDrafts",
            label: t(
              "todoList.tabs.evidenceDrafts.table.headers.hasLocationDrafts",
            ),
            options: [
              {
                label: t(
                  "todoList.tabs.evidenceDrafts.table.hasLocationDrafts.yes",
                ),
                value: "true",
              },
              {
                label: t(
                  "todoList.tabs.evidenceDrafts.table.hasLocationDrafts.no",
                ),
                value: "false",
              },
            ],
            type: "radioButton",
          },
        ],
        label: t(
          "todoList.tabs.evidenceDrafts.table.headers.hasLocationDrafts",
        ),
      },
      {
        filterOptions: [
          {
            key: "hasApprovedEvidence",
            label: t(
              "todoList.tabs.evidenceDrafts.table.headers.hasApprovedEvidence",
            ),
            options: [
              {
                label: t(
                  "todoList.tabs.evidenceDrafts.table.hasLocationDrafts.yes",
                ),
                value: "true",
              },
              {
                label: t(
                  "todoList.tabs.evidenceDrafts.table.hasLocationDrafts.no",
                ),
                value: "false",
              },
            ],
            type: "radioButton",
          },
        ],
        label: t(
          "todoList.tabs.evidenceDrafts.table.headers.hasApprovedEvidence",
        ),
      },
      {
        filterOptions: [
          {
            key: "hasLocationApproved",
            label: translate.t(
              "todoList.tabs.evidenceDrafts.table.headers.hasLocationApproved",
            ),
            options: [
              {
                label: t(
                  "todoList.tabs.evidenceDrafts.table.hasLocationDrafts.yes",
                ),
                value: "true",
              },
              {
                label: t(
                  "todoList.tabs.evidenceDrafts.table.hasLocationDrafts.no",
                ),
                value: "false",
              },
            ],
            type: "radioButton",
          },
        ],
        label: translate.t(
          "todoList.tabs.evidenceDrafts.table.headers.hasLocationApproved",
        ),
      },
    ],
    [t, uniqueGroups, uniqueTitles],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues("evidence_drafts") as string | null;
  }, [getValues]);
  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("evidence_drafts", value);
    },
    [setValues],
  );

  return useFilters({
    dataset: findings,
    getValues: handleGetValues,
    options,
    setFilteredDataset,
    setValues: handleSetValues,
  });
};

export { useEvidenceDraftsFilters };
